import { useState } from 'react';
import { Link } from 'react-router-dom';

const CONSULTATION_TOPICS = [
  { id: 'topic_bookkeeping', label: '記帳代行について' },
  { id: 'topic_app', label: '会計アプリの使い方' },
  { id: 'topic_hp', label: 'ホームページ作成' },
  { id: 'topic_tax', label: '確定申告について' },
  { id: 'topic_it', label: 'ITツール導入について' },
  { id: 'topic_other', label: 'その他' },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleTopicChange = (label: string) => {
    setSelectedTopics(prev =>
      prev.includes(label) ? prev.filter(t => t !== label) : [...prev, label]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      if (selectedTopics.length > 0) {
        formBody.append('consultation_topics', selectedTopics.join('、'));
      }

      const response = await fetch('https://readdy.ai/api/form/d6uorpvb245v7h34of5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setSelectedTopics([]);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-r from-[#0EA5E9] to-[#0EA5E9]/80 relative overflow-hidden">
      <div className="absolute right-0 top-0 w-96 h-96 bg-[#1A2332] rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute right-20 bottom-0 w-64 h-64 bg-[#1A2332] rounded-full opacity-30 blur-2xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl font-extrabold text-white leading-tight mb-8">
              まずは無料相談から、<br />
              始めてみませんか？
            </h2>
            
            <p className="text-lg text-white/90 mb-8">
              お電話・オンラインどちらでも対応可能。<br />
              経理やITのお悩み、お気軽にご相談ください。
            </p>

            <div className="space-y-6">
              <Link to="/company" className="flex items-center gap-4 hover:opacity-80 transition-opacity cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">お電話でのお問い合わせ</p>
                  <p className="text-white/80 text-sm">平日 9:00-18:00 ｜ 事業所情報はこちら →</p>
                </div>
              </Link>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">フォームでのお問い合わせ</p>
                  <p className="text-white/80 text-sm">24時間受付中</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-video-line text-2xl text-white"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">オンライン相談</p>
                  <p className="text-white/80 text-sm">Zoom・Google Meetに対応</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-[#1A2332] mb-6">お問い合わせフォーム</h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent text-sm"
                  placeholder="山田花子"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent text-sm"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent text-sm"
                  placeholder="090-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  屋号・お仕事の種類
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent text-sm"
                  placeholder="例：〇〇ネイルサロン / 〇〇接骨院 / ヨガインストラクター"
                />
              </div>

              {/* Consultation Topics Checkboxes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  ご相談の内容（複数選択可）
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {CONSULTATION_TOPICS.map(topic => (
                    <label
                      key={topic.id}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                        selectedTopics.includes(topic.label)
                          ? 'border-[#0EA5E9] bg-[#0EA5E9]/8 text-[#0EA5E9]'
                          : 'border-gray-200 hover:border-[#0EA5E9]/40 text-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        name="consultation_topics"
                        value={topic.label}
                        checked={selectedTopics.includes(topic.label)}
                        onChange={() => handleTopicChange(topic.label)}
                        className="accent-[#0EA5E9] w-4 h-4 shrink-0"
                      />
                      <span className="text-xs font-medium leading-tight">{topic.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none text-sm"
                  placeholder="その他、ご不明な点があればお気軽にどうぞ（500文字以内）"
                ></textarea>
                <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500文字</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1A2332] text-white py-4 rounded-lg font-semibold hover:bg-[#0EA5E9] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  送信に失敗しました。もう一度お試しください。
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}